import { useState } from 'react';
import { Card, formatBusinessDate, PageHeader, StateCard } from '@ampersandbrands/ui';
import { useStoreMe, useEffectiveStoreNo, isNotAvailable } from '@/lib/useStores';
import {
  usePerformance,
  type Cell,
  type PerformanceData,
  type TrendPoint,
} from '@/lib/usePerformance';
import { usePerformanceHourly } from '@/lib/usePerformanceHourly';
import { HourlyPanel } from '@/components/PerformanceHourly';

/**
 * The period tabs.
 *
 * `hourly` is the odd one out and deliberately so: it reads a different
 * endpoint, refreshed every 15 minutes from the registers, and renders its own
 * hour-of-day layout. The other three are windows over the same nightly OMNI
 * figures and share one layout. Keeping them in a single strip is what a store
 * expects, since to the reader they are all just "how are we doing".
 */
type TabKey = 'hourly' | 'yesterday' | 'wtd' | 'mtd';
type WindowKey = Exclude<TabKey, 'hourly'>;

const WINDOW_TABS: { key: TabKey; label: string }[] = [
  { key: 'hourly', label: 'Hourly' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'wtd', label: 'Week to date' },
  { key: 'mtd', label: 'Month to date' },
];

const WINDOW_NOUN: Record<WindowKey, string> = {
  yesterday: 'yesterday',
  wtd: 'week to date',
  mtd: 'month to date',
};

// --- Formatting -------------------------------------------------------------

function money(value: number | null, cents = false): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(value);
}

function moneyCompact(value: number | null): string {
  if (value === null) return '—';
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`;
  }
  return money(value);
}

function count(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('en-US');
}

function pct(ratio: number | null, digits = 1): string {
  if (ratio === null) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

// --- Delta chips ------------------------------------------------------------

const UP = 'text-emerald-600 dark:text-emerald-400';
const DOWN = 'text-rose-600 dark:text-rose-400';
const FLAT = 'text-candy-ink-muted dark:text-candy-dark-ink-muted';

/** Direction styling for a signed delta (higher is always better here). */
function tone(value: number | null): { cls: string; arrow: string } {
  if (value === null || value === 0) return { cls: FLAT, arrow: '·' };
  return value > 0 ? { cls: UP, arrow: '▲' } : { cls: DOWN, arrow: '▼' };
}

/** A soft pill for the hero (percent comp + a plain-language basis). */
function HeroChip({ comp, label }: { comp: number | null; label: string }) {
  const t = tone(comp);
  const soft =
    comp === null || comp === 0
      ? 'bg-candy-cream-warm dark:bg-candy-dark-surface-warm'
      : comp > 0
        ? 'bg-emerald-50 dark:bg-emerald-950/40'
        : 'bg-rose-50 dark:bg-rose-950/40';
  return (
    <span className={`inline-flex items-baseline gap-1.5 rounded-pill px-3 py-1.5 text-[13px] font-semibold ${soft} ${t.cls}`}>
      <span aria-hidden className="text-[11px]">{t.arrow}</span>
      {comp === null ? '—' : pct(Math.abs(comp))}
      <span className="font-medium opacity-80">{label}</span>
    </span>
  );
}

/**
 * Hero chip basis, with last year's dollar total in it (ISD-515), the way the
 * Hourly tab already shows it. Falls back to the plain basis when there is no
 * last-year figure, so the chip never reads "vs — last year".
 */
function lyLabel(window: WindowKey, ly: number | null): string {
  const basis = window === 'yesterday' ? 'same weekday last year' : 'same days last year';
  return ly === null ? `vs ${basis}` : `vs ${money(ly)} ${basis}`;
}

/** Inline colored mini-chip for KPI cards. */
function MiniChip({ value, text }: { value: number | null; text: string }) {
  const t = tone(value);
  return (
    <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
      <span className={`font-bold ${t.cls}`}>
        <span aria-hidden>{t.arrow}</span> {text}
      </span>
    </span>
  );
}

// --- Trend chart (this year vs same-weekday last year) ----------------------

function TrendChart({ daily }: { daily: TrendPoint[] }) {
  const W = 640;
  const H = 150;
  const padL = 6;
  const padR = 10;
  const padT = 14;
  const padB = 16;
  const n = daily.length;

  const values: number[] = [];
  for (const d of daily) {
    values.push(d.ty);
    if (d.ly !== null) values.push(d.ly);
  }
  const rawMax = values.length ? Math.max(...values) : 1;
  const rawMin = values.length ? Math.min(...values) : 0;
  const max = rawMax * 1.06 || 1;
  const min = rawMin * 0.9;
  const span = max - min || 1;

  const x = (i: number) => padL + ((W - padL - padR) * (n > 1 ? i / (n - 1) : 0));
  const y = (v: number) => padT + (H - padT - padB) * (1 - (v - min) / span);

  const tyLine = daily.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.ty).toFixed(1)}`).join(' ');
  const area = `${tyLine} L${x(n - 1).toFixed(1)},${(H - padB).toFixed(1)} L${x(0).toFixed(1)},${(H - padB).toFixed(1)} Z`;

  let lyLine = '';
  let started = false;
  daily.forEach((d, i) => {
    if (d.ly === null) return;
    lyLine += `${started ? 'L' : 'M'}${x(i).toFixed(1)},${y(d.ly).toFixed(1)} `;
    started = true;
  });

  const lastTy = daily[n - 1]?.ty ?? 0;

  return (
    <div>
      <div className="mb-1.5 flex gap-4 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-[3px] w-3.5 rounded bg-candy-pink dark:bg-candy-pink-dark" /> This year
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-[3px] w-3.5 rounded bg-candy-ink-muted dark:bg-candy-dark-ink-muted" /> Last year (same weekday)
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[150px] w-full" role="img" aria-label="14-day net sales, this year versus last year">
        <defs>
          <linearGradient id="tyArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E85D9E" stopOpacity="0.26" />
            <stop offset="100%" stopColor="#E85D9E" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 1, 2].map((g) => {
          const gy = padT + (H - padT - padB) * (g / 2);
          return (
            <line key={g} x1={padL} y1={gy} x2={W - padR} y2={gy} className="stroke-candy-line dark:stroke-candy-dark-line" strokeWidth={1} />
          );
        })}
        {lyLine && (
          <path d={lyLine} fill="none" strokeDasharray="4 4" strokeWidth={1.6} className="stroke-candy-ink-muted dark:stroke-candy-dark-ink-muted" />
        )}
        <path d={area} fill="url(#tyArea)" />
        <path d={tyLine} fill="none" strokeWidth={2.4} strokeLinejoin="round" className="stroke-candy-pink dark:stroke-candy-pink-dark" />
        <circle cx={x(n - 1)} cy={y(lastTy)} r={5.5} className="fill-candy-white dark:fill-candy-dark-surface" />
        <circle cx={x(n - 1)} cy={y(lastTy)} r={3.5} className="fill-candy-pink dark:fill-candy-pink-dark" />
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        <span>{formatBusinessDate(daily[0]?.date, { short: true })}</span>
        <span>{formatBusinessDate(daily[n - 1]?.date, { short: true })}</span>
      </div>
    </div>
  );
}

// --- KPI card ---------------------------------------------------------------

function KpiCard({ label, value, chip }: { label: string; value: string; chip: React.ReactNode }) {
  return (
    <Card className="p-4">
      <div className="text-[13px] font-semibold text-candy-ink-muted dark:text-candy-dark-ink-muted">{label}</div>
      <div className="my-1.5 text-[26px] font-bold leading-none tracking-tight text-candy-ink dark:text-candy-dark-ink">{value}</div>
      <div>{chip}</div>
    </Card>
  );
}

// --- Category momentum ------------------------------------------------------

function CategoryPanel({ data }: { data: PerformanceData['categories'] }) {
  const rows = data.rows.slice(0, 6);
  const maxTy = rows.reduce((m, r) => Math.max(m, r.ty), 0) || 1;
  const monthsLabel = data.months === 1 ? 'the last complete month' : `the last ${data.months} months`;

  if (rows.length === 0) {
    return (
      <Card className="p-5">
        <h2 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">Where you're winning and slipping</h2>
        <p className="mt-3 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">No category sales in this window yet.</p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">Where you're winning and slipping</h2>
      <p className="mb-4 mt-0.5 text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Net sales by category over {monthsLabel}, and the change vs the same window last year.
      </p>
      <div className="grid gap-3">
        {rows.map((r) => {
          const t = tone(r.comp);
          return (
            <div key={r.category} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 sm:grid-cols-[132px_1fr_auto]">
              <div className="min-w-0">
                <div className="truncate text-[14px] font-semibold text-candy-ink dark:text-candy-dark-ink">{r.category}</div>
                <div className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">{pct(r.pct, 0)} of sales</div>
              </div>
              <div className="order-last col-span-2 h-2.5 overflow-hidden rounded-pill bg-candy-cream-warm dark:bg-candy-dark-surface-warm sm:order-none sm:col-span-1">
                <span
                  className="block h-full rounded-pill bg-gradient-to-r from-candy-pink to-[#D6488A]"
                  style={{ width: `${Math.max(4, (r.ty / maxTy) * 100)}%` }}
                />
              </div>
              <div className="text-right">
                <div className={`text-[14px] font-bold ${t.cls}`}>
                  <span aria-hidden>{t.arrow}</span> {r.comp === null ? '—' : pct(Math.abs(r.comp))}
                </div>
                <div className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">{money(r.ty)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {data.biggest_slip && (
        <div className="mt-4 flex items-center gap-2.5 rounded-lg border border-candy-gold/40 bg-candy-gold/10 px-4 py-3 text-[14px] text-candy-ink dark:text-candy-dark-ink">
          <span aria-hidden>🍫</span>
          <span>
            <b className="font-semibold">Focus:</b> {data.biggest_slip.category} is down {money(data.biggest_slip.decline)} vs last year,
            the biggest slip in this window.
          </span>
        </div>
      )}
    </Card>
  );
}

// --- Month-to-date pace + fleet rank ---------------------------------------

function medal(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return '📈';
}

/**
 * Month-to-date pace.
 *
 * The goal progress bar and its percent-to-goal line came out on 2026-08-12,
 * when the plan and budget-percent figures were removed from the OMNI
 * calculation the whole report reads from. Until the sales-plan work from that
 * meeting lands there is no goal to track toward, so the card shows sales so far
 * this month against last year, plus the fleet rank.
 */
function PaceCard({ data }: { data: PerformanceData }) {
  const mtd = data.sections.sales.mtd;
  const vsLy = tone(mtd.comp_week);
  const fr = data.fleet_rank;

  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">Month-to-date pace</h2>
      <p className="mb-3 mt-0.5 text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Sales so far this month.
      </p>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-[30px] font-bold tracking-tight text-candy-ink dark:text-candy-dark-ink">{moneyCompact(mtd.ty)}</span>
      </div>
      <ul className="mt-4 grid list-none gap-2.5 p-0">
        <li className="flex justify-between text-[14px]">
          <span className="text-candy-ink-muted dark:text-candy-dark-ink-muted">MTD vs last year</span>
          <span className={`font-bold ${vsLy.cls}`}>
            <span aria-hidden>{vsLy.arrow}</span> {mtd.comp_week === null ? '—' : pct(Math.abs(mtd.comp_week))}
          </span>
        </li>
      </ul>
      {fr && (
        <div className="mt-4 flex items-center gap-3 border-t border-candy-line pt-4 dark:border-candy-dark-line">
          <span className="text-[26px]" aria-hidden>{medal(fr.rank)}</span>
          <span className="text-[14px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            <b className="text-candy-ink dark:text-candy-dark-ink">
              {fr.rank === 1 ? '1st' : fr.rank === 2 ? '2nd' : fr.rank === 3 ? '3rd' : `${fr.rank}th`} of {fr.of} stores
            </b>{' '}
            for net sales this month.
          </span>
        </div>
      )}
    </Card>
  );
}

// --- Page -------------------------------------------------------------------

/**
 * The page header no longer claims a single freshness for the whole page,
 * because the Hourly tab and the other three no longer share one. Each tab
 * states its own currency: Hourly says it refreshes through the day, the
 * others show the business date they cover.
 */
function Head({ storeName, subtitle }: { storeName?: string; subtitle: string }) {
  return <PageHeader eyebrow="Store performance" title={`How ${storeName ?? 'your store'} is doing`} subtitle={subtitle} />;
}

export function Performance() {
  const storeNo = useEffectiveStoreNo();
  // The page opens on Hourly (Steven, 2026-09-21). A store checking the numbers
  // is almost always mid-shift and asking how today is going, and landing on
  // yesterday made them tap before they could see it. Before the first sale the
  // tab says so plainly and still shows where last year's day ended up, so an
  // early-morning visit is not a dead end.
  const [activeTab, setActiveTab] = useState<TabKey>('hourly');
  const isHourly = activeTab === 'hourly';
  // The three daily tabs all read the same payload; `hourly` borrows a window
  // key only so the shared code below stays total.
  const activeWindow: WindowKey = isHourly ? 'yesterday' : activeTab;

  const meQ = useStoreMe(storeNo);
  const perfQ = usePerformance(storeNo);
  // Only fetched once the reader actually opens the tab, so the common case
  // (someone checking yesterday) costs no extra call.
  const hourlyQ = usePerformanceHourly(storeNo, isHourly);
  const storeName = meQ.data?.store?.store_name;

  const subtitle = isHourly
    ? 'Today as it happens, hour by hour'
    : perfQ.data?.as_of
      ? formatBusinessDate(perfQ.data.as_of)
      : 'Sales, traffic, and category momentum';

  const tabStrip = (
    <div className="inline-flex flex-wrap gap-0.5 rounded-lg border border-candy-line bg-candy-cream-warm p-1 dark:border-candy-dark-line dark:bg-candy-dark-surface-warm">
      {WINDOW_TABS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={activeTab === key}
          onClick={() => setActiveTab(key)}
          className={`rounded-md px-4 py-2 text-[14px] font-semibold transition-colors ${
            activeTab === key
              ? 'bg-candy-white text-candy-ink shadow-1 dark:bg-candy-dark-surface dark:text-candy-dark-ink'
              : 'text-candy-ink-muted hover:text-candy-ink dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const shell = (children: React.ReactNode) => (
    <div className="mx-auto w-full max-w-5xl pb-24">
      <Head storeName={storeName} subtitle={subtitle} />
      {children}
    </div>
  );

  const skeleton = (
    <div className="space-y-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-28 animate-pulse rounded-lg bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
      ))}
    </div>
  );

  // The Hourly tab stands on its own data, so it renders before the OMNI
  // payload is consulted. A store should still see today's trading when the
  // nightly report is missing or late.
  if (isHourly) {
    return shell(
      <div className="space-y-4">
        {tabStrip}
        {meQ.isLoading || hourlyQ.isLoading ? (
          skeleton
        ) : hourlyQ.error && isNotAvailable(hourlyQ.error) ? (
          <StateCard
            dashed
            title="Not available yet"
            description="Hourly sales aren't available for this store yet."
          />
        ) : hourlyQ.error ? (
          <StateCard
            tone="error"
            title="Could not load today's hours"
            description="Something went wrong loading this store's hourly numbers. Please try again."
          />
        ) : hourlyQ.data ? (
          <HourlyPanel data={hourlyQ.data} />
        ) : (
          skeleton
        )}
        <p className="pt-2 text-center text-[12.5px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Today's sales, including tickets still open at the register. The registers report in once an hour,
          so the newest hour can take a little while to appear. Net sales exclude tax, and last year is the
          matching weekday 364 days back.
        </p>
      </div>,
    );
  }

  if (meQ.isLoading || perfQ.isLoading) {
    return shell(skeleton);
  }

  if (perfQ.error && isNotAvailable(perfQ.error)) {
    return shell(<StateCard dashed title="Not available yet" description="Performance data isn't available for this store yet." />);
  }

  if (perfQ.error ?? meQ.error) {
    return shell(<StateCard tone="error" title="Could not load performance" description="Something went wrong loading this store's numbers. Please try again." />);
  }

  const data = perfQ.data!;
  const s = data.sections;
  const salesCell: Cell = s.sales[activeWindow];
  const conv = s.traffic[activeWindow].conversion;
  const dpt = s.dpt_upt[activeWindow].dpt;
  const upt = s.dpt_upt[activeWindow].upt;

  const convPoints = conv.ty !== null && conv.ly_week !== null ? (conv.ty - conv.ly_week) * 100 : null;
  const uptDelta = upt.ty !== null && upt.ly_week !== null ? upt.ty - upt.ly_week : null;

  return shell(
    <div className="space-y-4">
      {/* Period tabs */}
      {tabStrip}

      {/* Hero: net sales + both LY deltas + trend */}
      <Card className="grid grid-cols-1 items-center gap-6 p-5 md:grid-cols-2">
        <div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Net sales · {WINDOW_NOUN[activeWindow]}
          </div>
          <div className="my-3 text-[clamp(44px,6vw,60px)] font-extrabold leading-none tracking-tight text-candy-ink dark:text-candy-dark-ink">
            {money(salesCell.ty)}
          </div>
          <div className="flex flex-wrap gap-2.5">
            <HeroChip comp={salesCell.comp_week} label={lyLabel(activeWindow, salesCell.ly_week)} />
          </div>
        </div>
        <div>{data.daily.length > 0 && <TrendChart daily={data.daily} />}</div>
      </Card>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard
          label="Transactions"
          value={count(s.transactions[activeWindow].ty)}
          chip={<MiniChip value={s.transactions[activeWindow].comp_week} text={`${s.transactions[activeWindow].comp_week === null ? '—' : pct(Math.abs(s.transactions[activeWindow].comp_week))} vs LY`} />}
        />
        <KpiCard
          label="Traffic"
          value={count(s.traffic[activeWindow].ty)}
          chip={
            s.traffic[activeWindow].ty === null ? (
              <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">No door count yet</span>
            ) : (
              <MiniChip value={s.traffic[activeWindow].comp_week} text={`${s.traffic[activeWindow].comp_week === null ? '—' : pct(Math.abs(s.traffic[activeWindow].comp_week))} counted at the door`} />
            )
          }
        />
        <KpiCard
          label="Conversion"
          value={pct(conv.ty)}
          chip={<MiniChip value={convPoints} text={`${convPoints === null ? '—' : `${Math.abs(convPoints).toFixed(1)} pt`} visits that bought`} />}
        />
        <KpiCard
          label="Avg ticket"
          value={money(dpt.ty, true)}
          chip={<MiniChip value={dpt.comp_week} text={`${dpt.comp_week === null ? '—' : pct(Math.abs(dpt.comp_week))} per transaction`} />}
        />
        <KpiCard
          label="Items / ticket"
          value={upt.ty === null ? '—' : upt.ty.toFixed(1)}
          chip={<MiniChip value={uptDelta} text={`${uptDelta === null ? '—' : Math.abs(uptDelta).toFixed(1)} · ${count(s.units[activeWindow].ty)} items sold`} />}
        />
      </div>

      {/* Lower: category momentum + MTD pace */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <CategoryPanel data={data.categories} />
        </div>
        <div>
          <PaceCard data={data} />
        </div>
      </div>

      <p className="pt-2 text-center text-[12.5px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Net sales exclude tax and shipping. Last-year figures use the matching weekday 364 days back.
        For today, open the Hourly tab.
      </p>
    </div>,
  );
}
